import { useCallback, useState } from 'react';
import { toast } from 'material-react-toastify';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useThemeColor } from '../../../../hooks/useThemeColor';
import { useUserSummary } from '../../../../hooks/useUserSummary';
import { getEmptyRenewPasswordRequest } from '../../../../interfaces/Auth/RenewPasswordRequest';
import { renewPassword } from '../../../../api/Auth';
import { validatePassword } from '../../../../utils/Validation';
import { bodyWidth } from '../../../../utils/Style';
import { AccountNavbar } from '../../AccountNavbar';
import { GenericHelmet } from '../../../../components/Helmet/GenericHelmet';
import { useLocalizedText } from '../../../../hooks/useLocalizedText';

export const RenewPasswordPage = () => {
  const t = useLocalizedText();

  const navigate = useNavigate();

  const { primary, secondary } = useThemeColor();

  const { userSummary } = useUserSummary();

  const [values, setValues] = useState(getEmptyRenewPasswordRequest());
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(() => {
    if (!userSummary) {
      return;
    }

    setLoading(true);
    renewPassword({ ...values, email: userSummary.email })
      .then(() => {
        toast.success('Şifre başarıyla değiştirildi.');
        navigate('/hesabim');
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [userSummary, values]);

  const isOldPasswordValid = values.oldPassword.length > 0;
  const isPasswordValid = validatePassword(values.newPassword);
  const isConfirmPasswordValid = values.newPassword === values.confirmPassword;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
      }}
    >
      <GenericHelmet title={t('account.renew_password.password_change')} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: bodyWidth,
          pb: 3,
          px: 1.5,
          gap: 1.5,
          boxSizing: 'border-box',
        }}
      >
        <AccountNavbar selectedTab='password' />

        <Paper
          elevation={3}
          sx={{
            px: 2.5,
            py: 2,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '100%',
            maxWidth: 800,
          }}
        >
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>
            {t('account.renew_password.password_change')}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              width: '100%',
            }}
          >
            <Typography>
              {t('account.renew_password.current_password')}
            </Typography>
            <TextField
              size='small'
              fullWidth
              disabled={loading}
              type='password'
              value={values.oldPassword}
              onChange={(e) =>
                setValues({ ...values, oldPassword: e.target.value })
              }
              error={!isOldPasswordValid}
              helperText={
                isOldPasswordValid
                  ? undefined
                  : t('validation.field_is_required')
              }
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              width: '100%',
            }}
          >
            <Typography>{t('account.renew_password.new_password')}</Typography>
            <TextField
              size='small'
              fullWidth
              disabled={loading}
              type='password'
              value={values.newPassword}
              onChange={(e) =>
                setValues({ ...values, newPassword: e.target.value })
              }
              error={!isPasswordValid}
              helperText={
                !values.newPassword.length || isPasswordValid
                  ? undefined
                  : t('validation.password_length')
              }
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              width: '100%',
            }}
          >
            <Typography>
              {t('account.renew_password.confirm_password')}
            </Typography>
            <TextField
              size='small'
              fullWidth
              disabled={loading}
              type='password'
              value={values.confirmPassword}
              onChange={(e) =>
                setValues({ ...values, confirmPassword: e.target.value })
              }
              error={!!values.newPassword.length && !isConfirmPasswordValid}
              helperText={
                isConfirmPasswordValid || !values.newPassword.length
                  ? undefined
                  : t('validation.passwords_does_not_match')
              }
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Button
              variant='contained'
              onClick={handleSubmit}
              disabled={
                loading ||
                !isPasswordValid ||
                !isOldPasswordValid ||
                !isConfirmPasswordValid
              }
              sx={{
                textTransform: 'none',
                backgroundColor: primary,
                color: secondary,
              }}
            >
              {t('common.save')}
            </Button>

            {loading && <CircularProgress color='primary' />}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};
